import { exitHint } from './ui/prompts';
import { SynthAudio } from './audio/audio';
import { buildRegistry } from './content/index';
import type { ContentRegistry } from './content/registry';
import { FixedLoop, SIM_DT } from './core/fixedLoop';
import { InputManager, screenToWorldMovement, wheelZoomDelta } from './core/input';
import { botInput, botPickUpgrade } from './core/bot';
import { createBackend, dailyKey, type CloudBackend } from './meta/backend';
import { applyRunResult, buildChallengeRunConfig, buildDailyRunConfig, buildRunConfig, type RunResult } from './meta/profile';
import { loadLocal, mergeSaves, saveLocal, type ColorblindMode, type ControlsHudMode, type SaveData } from './meta/save';
import { GameView } from './render/view';
import { INTRO_SECONDS, Sim } from './sim/sim';
import { DebugOverlay } from './ui/debug';
import { h } from './ui/dom';
import { Hud } from './ui/hud';
import { DamageNumbers } from './ui/damageNumbers';
import { actionsForKey } from './core/bindings';
import type { ControlsActions } from './ui/controlsPanel';
import { setPromptBindings, trackDeviceInput } from './ui/inputPrompts';
import { GamepadNav } from './ui/gamepadNav';
import { challengeScreen, hubScreen, leaderboardScreen, levelUpScreen, mainMenu, observatoryBuildScreen, observatoryHud, observatoryPauseScreen, observatoryPlacementHud, observatoryUpgradeScreen, pauseScreen, resultsScreen, runSetupScreen, type HubTab, type RunSetupOptions } from './ui/screens';
import { isTouchDevice, TouchControls } from './ui/touch';
import { applyEraTheme, eraThemeFor } from './ui/theme';
import { clampCameraDistance } from './render/renderer';
import { clearRun, loadRun, restoreConfig, saveRun, storedSnapshot } from './meta/runPersistence';
import { loadReplay, recorderFor, ReplayPlayer, ReplayRecorder, saveReplay, type ReplayData } from './meta/replay';
import { restore } from './sim/snapshot';
import type { SimCommand } from './sim/sim';
import { updateAchievements, ACHIEVEMENTS } from './content/achievements';
import { emptyInput } from './core/input';
import { clampCoopSettings, loadoutFromSave } from './net/coopConfig';
import { CoopGuestSession, CoopHostSession, type CoopCallbacks, type CoopSession } from './net/session';
import { CloudLobbyTransport, LocalLobbyTransport, lobbyApi, type LobbyTransport } from './net/webrtc';
import { lobbyScreen, onlineEntryScreen, waitingBanner } from './ui/coopScreens';
import { CHALLENGE_BY_ID } from './content/challenges';
import { ObservatorySession, type ObservatoryStation } from './house/observatory';
import { characterDef } from './content/characters';
import type { ItemDef } from './content/types';
import { HOUSE_MODULE_BY_ID } from './house/modules';

import { GAME_VERSION } from './version';
export { GAME_VERSION };

type Screen = 'menu' | 'runSetup' | 'hub' | 'leaderboards' | 'challenges' | 'run' | 'levelup' | 'stageClear' | 'paused' | 'results' | 'timeJump' | 'attract' | 'replay' | 'online' | 'lobby' | 'coopWait' | 'observatory' | 'observatoryPaused' | 'observatoryMenu';

/** `?coop=local`: online co-op between windows of this browser, no server (development). */
const LOCAL_COOP = typeof location !== 'undefined' && new URLSearchParams(location.search).get('coop') === 'local';
const COOP_NAME_KEY = 'chronofall.coopName';

/** Seconds of no input on the main menu before the attract-mode demo starts. */
const ATTRACT_IDLE_SEC = 90;

/**
 * Game: top-level state machine + glue between Sim, View, UI, Save and Backend.
 *
 *   menu ─▶ hub ─▶ menu ─▶ run ─▶ (levelup | stageClear | paused | timeJump)* ─▶ results ─▶ menu
 *   menu ─(idle 90 s)─▶ attract (bot-driven demo run) ─(any input)─▶ menu
 *
 * The sim only ticks while screen === 'run' (or 'attract', driven by the bot). Every other screen is a pause by
 * construction, so the sim never needs to know about UI.
 */
export class Game {
  readonly reg: ContentRegistry;
  readonly loop: FixedLoop;
  readonly input: InputManager;
  readonly view: GameView;
  readonly audio = new SynthAudio();
  readonly hud: Hud;
  readonly debug: DebugOverlay;
  readonly gamepadNav: GamepadNav;
  readonly touch: TouchControls | null = null;
  save: SaveData;
  backend!: CloudBackend;
  sim: Sim | null = null;
  private screen: Screen = 'menu';
  private ui: HTMLElement;
  private overlay: HTMLElement | null = null;
  private runResult: RunResult | null = null;
  private isDaily = false;
  private dailyRunKey: string | null = null;
  private dailyPractice = false;
  private challengeId: string | null = null;
  private cloudStatus = 'Local save only';
  private saveTimer = 0;
  private runSaveTimer = 0;
  private recorder: ReplayRecorder | null = null;
  private replayPlayer: ReplayPlayer | null = null;
  /** Preserved while the player visits Timeline loadout tabs from pre-run setup. */
  private pendingRunSetup: RunSetupOptions | null = null;
  private zoomPersistTimer = -1;
  /** Attract mode: seconds since the last input while on the menu. */
  private idleSec = 0;
  /* ---- online co-op (src/net) ---- */
  private coop: CoopSession | null = null;
  private coopLobby: { refresh(): void } | null = null;
  private coopLobbyTimer = 0;
  /** The hub was opened from the lobby ("Change loadout"). */
  private pendingLobby = false;
  /** Identity of the currently mounted online-run overlay, so it is only rebuilt on change. */
  private coopScreenKey = '';
  /** A local command is in flight (it applies a few frames later on every peer). */
  private coopPickPending = false;
  private readonly coopInput = [emptyInput()];
  /** Smoothed cost of one online step (lockstep + sim), for the debug net panel. */
  private coopStepMs = 0;
  private observatorySession: ObservatorySession | null = null;
  private readonly observatoryInput = [emptyInput()];
  private returnToObservatory = false;

  constructor(canvas: HTMLCanvasElement, ui: HTMLElement) {
    this.ui = ui;
    this.reg = buildRegistry();
    this.save = loadLocal();
    this.input = new InputManager();
    trackDeviceInput();
    this.applyControls();
    this.view = new GameView(canvas, this.reg, this.audio, ui);
    this.view.renderer.setQuality(this.save.settings.quality);
    this.view.renderer.setCameraDistance(this.save.settings.cameraDistance);
    this.view.renderer.reducedFlash = this.save.settings.reducedFlash;
    this.view.shakeEnabled = this.save.settings.screenShake;
    this.view.damageNumbers = new DamageNumbers(ui, this.view.renderer);
    this.view.damageNumbers.enabled = this.save.settings.showDamageNumbers;
    this.audio.setMusicVolume(this.save.settings.musicVolume);
    this.audio.setSfxVolume(this.save.settings.sfxVolume);
    ui.appendChild(h('div', { id: 'version' }, `v${GAME_VERSION}`));
    this.hud = new Hud(ui);
    this.hud.setControlsMode(this.save.settings.controlsHud, this.save.stats.runs);
    this.hud.setVisible(false);
    this.debug = new DebugOverlay(
      ui,
      (this.loop = new FixedLoop({ tick: (dt) => this.tick(dt), render: (a, dt) => this.render(a, dt) })),
      this.reg,
      () => this.sim,
      (visible) => {
        this.input.captured = visible || (this.screen !== 'run' && this.screen !== 'observatory');
        this.touch?.setVisible((this.screen === 'run' || this.screen === 'observatory') && !visible);
      },
    );
    this.view.onAnnounce = (t) => this.hud.toast(t);

    this.input.onKeyDown = (code) => this.hotkey(code);
    for (const evt of ['keydown', 'pointerdown', 'pointermove', 'wheel'] as const) window.addEventListener(evt, () => this.onAnyInput(evt), { passive: true });
    window.addEventListener('wheel', (e) => {
      if ((this.screen !== 'run' && this.screen !== 'observatory') || this.debug.visible) return;
      e.preventDefault();
      this.adjustCameraDistance(wheelZoomDelta(e.deltaY, e.deltaMode));
    }, { passive: false });
    window.addEventListener('pointerdown', () => this.audio.resume(), { once: false });
    window.addEventListener('keydown', () => this.audio.resume(), { once: false });
    window.addEventListener('pagehide', () => this.saveActiveRun());

    this.gamepadNav = new GamepadNav({
      currentOverlay: () => this.currentOverlay(),
      pressEscape: () => this.hotkey('Escape'),
      togglePause: () => {
        if (this.screen === 'run') this.pause();
        else if (this.screen === 'observatory') this.pauseObservatory();
        else if (this.screen === 'paused') this.mount(null, 'run');
        else if (this.screen === 'observatoryPaused') this.resumeObservatory();
      },
      pauseButtons: () => this.save.settings.bindings.gamepad.pause,
      onPadActivity: () => this.onAnyInput('gamepad'),
      menuKeys: () => {
        const k = this.save.settings.bindings.keyboard;
        return { up: k.moveUp, down: k.moveDown, left: k.moveLeft, right: k.moveRight };
      },
    });
    if (isTouchDevice()) this.touch = new TouchControls(ui, this.input, () => this.hotkey('Escape'), (delta) => this.adjustCameraDistance(delta));
  }

  /** The currently mounted overlay element, or null while running with no overlay (src/ui/gamepadNav.ts). */
  currentOverlay(): HTMLElement | null {
    return this.debug.visible ? this.debug.root : this.overlay;
  }

  async start(): Promise<void> {
    // GLTF drop-in pipeline: load every distinct visual.gltf model referenced
    // by content (see render/models.ts) before building the meshes that use
    // them (InstancedViews/Environment/player avatars).
    await Promise.all([this.view.models.preload(this.reg), this.view.characters.preload()]);
    this.view.init();
    this.applyPresentationSettings();
    this.view.setEra(this.reg.eras[0]);
    this.view.setPlayerCount(0);

    this.backend = await createBackend();
    this.backend.onAuthChange(() => void this.syncFromCloud());
    await this.syncFromCloud();
    const redirectError = (this.backend as { redirectError?: string | null }).redirectError;
    if (redirectError) this.cloudStatus = redirectError;
    this.applyPresentationSettings();
    this.showMenu();
    this.loop.start();
  }

  /* ---------------------------------------------------------------- save */

  private async syncFromCloud(): Promise<void> {
    if (!this.backend.currentUser()) {
      this.cloudStatus = this.backend.kind === 'local' ? 'Offline mode' : 'Not signed in';
      return;
    }
    this.cloudStatus = 'Syncing…';
    try {
      const cloud = await this.backend.loadSave();
      if (cloud) {
        const localUpdated = this.save.updatedAt;
        const cloudUpdated = cloud.updatedAt;
        this.save = mergeSaves(this.save, cloud);
        this.applyControls();
        this.cloudStatus = localUpdated === cloudUpdated ? 'Cloud save up to date' : 'Progress merged safely · newest preferences kept';
      } else {
        this.cloudStatus = 'This device uploaded to cloud';
      }
      saveLocal(this.save);
      await this.backend.storeSave(this.save);
    } catch (e) {
      console.warn('cloud sync failed', e);
      this.cloudStatus = 'Sync failed · local save is safe';
    }
  }

  /** Push the saved bindings into input sampling and every on-screen prompt. */
  private applyControls(): void {
    this.input.setControls(this.save.settings.bindings);
    setPromptBindings(this.save.settings.bindings);
  }

  /** Shared by the main-menu Settings sheet and the pause screen. */
  private controlsActions(): ControlsActions {
    return {
      get: () => this.save.settings.bindings,
      set: (next) => {
        this.save.settings.bindings = next;
        this.applyControls();
        this.persist();
      },
      suspendNav: (on) => { this.gamepadNav.suspended = on; },
    };
  }

  persist(): void {
    saveLocal(this.save);
    if (this.backend?.currentUser()) void this.backend.storeSave(this.save).catch((e: unknown) => console.warn('cloud save failed', e));
  }

  /* ---------------------------------------------------------------- screens */

  private mount(el: HTMLElement | null, screen: Screen): void {
    if (this.screen === 'run' && screen !== 'run' && screen !== 'results') this.saveActiveRun();
    if (screen !== 'run' && this.zoomPersistTimer >= 0) {
      this.zoomPersistTimer = -1;
      this.persist();
    }
    this.overlay?.remove();
    this.overlay = el;
    if (el) this.ui.appendChild(el);
    this.screen = screen;
    this.input.captured = screen !== 'run' && screen !== 'observatory';
    this.hud.setVisible(screen === 'run' || screen === 'paused' || screen === 'levelup' || screen === 'attract' || screen === 'replay' || screen === 'coopWait');
    this.hud.setControlsActive(screen === 'run');
    this.touch?.setVisible((screen === 'run' || screen === 'observatory') && !this.debug.visible);
  }

  showMenu(): void {
    this.leaveObservatory();
    this.leaveCoop();
    this.pendingRunSetup = null;
    this.sim = null;
    this.recorder = null;
    this.replayPlayer = null;
    this.view.skipTimeJump();
    this.view.setPlayerCount(0);
    this.view.setWarp(0);
    this.view.damageNumbers?.clear();
    this.view.setEra(eraThemeFor(this.save, this.reg));
    this.audio.setMusic('menu');
    this.idleSec = 0;
    applyEraTheme(eraThemeFor(this.save, this.reg));
    this.mount(
      mainMenu(this.save, this.reg, this.backend, {
        startRun: (o) => o.daily ? this.startRun(o) : this.showRunSetup({ ...o, daily: false }),
        resumeRun: () => this.resumeRun(),
        watchReplay: () => this.watchReplay(),
        hasSavedRun: loadRun(GAME_VERSION) !== null,
        hasReplay: loadReplay(GAME_VERSION) !== null,
        cloudStatus: this.cloudStatus,
        openHub: (tab) => this.showHub(tab),
        openLeaderboards: () => this.mount(leaderboardScreen(this.backend, () => this.showMenu(), (replay) => this.watchReplayData(replay, 'top run')), 'leaderboards'),
        openChallenges: () => this.mount(challengeScreen(this.save, (id) => this.startChallenge(id), () => this.showMenu()), 'challenges'),
        openOnline: () => this.showOnline(),
        updateRunSetup: (setup) => {
          this.save.runSetup = setup;
          this.persist();
        },
        setQuality: (q) => {
          this.save.settings.quality = q;
          this.view.renderer.setQuality(q);
          this.persist();
        },
        setMusicVolume: (v) => {
          this.save.settings.musicVolume = v;
          this.audio.setMusicVolume(v);
          this.persist();
        },
        setSfxVolume: (v) => {
          this.save.settings.sfxVolume = v;
          this.audio.setSfxVolume(v);
          this.persist();
        },
        setCameraDistance: (v) => this.setCameraDistance(v, true),
        setColorblindMode: (mode) => this.setColorblindMode(mode),
        setControlsHud: (mode) => this.setControlsHud(mode),
        setLocale: (locale) => { this.save.settings.locale = locale; this.persist(); this.showMenu(); },
        toggle: (key, on) => this.setToggle(key, on),
        controls: this.controlsActions(),
        signIn: (kind) => void (kind === 'google' ? this.backend.signInWithGoogle() : this.backend.signInAnonymously()).then(() => this.syncFromCloud()).then(() => this.showMenu()).catch((e: unknown) => alert((e as Error).message)),
        signOut: () => void this.backend.signOut().then(() => this.showMenu()),
        syncCloud: () => void this.syncFromCloud().then(() => this.showMenu()),
      }),
      'menu',
    );
  }

  showHub(initialTab: HubTab = 'characters'): void {
    if (initialTab === 'observatory') {
      this.showObservatory();
      return;
    }
    this.audio.setMusic('hub');
    this.mount(hubScreen(this.save, this.reg, this.view.characters, this.view.models, {
      back: () => this.hubBack(),
      changed: () => this.persist(),
      enterObservatory: () => this.showObservatory(),
    }, initialTab), 'hub');
  }

  private hubBack(): void {
    if (this.returnToObservatory && this.observatorySession) {
      this.returnToObservatory = false;
      this.resumeObservatory();
    } else if (this.pendingLobby && this.coop) {
      this.coop.setLoadout(loadoutFromSave(this.save, this.reg, this.coopName()));
      this.showLobby();
    } else if (this.pendingRunSetup) this.showRunSetup(this.pendingRunSetup);
    else this.showMenu();
  }

  private currentEquipment(): ItemDef[] {
    const ids = [this.save.equipment.weapon, this.save.equipment.armor, this.save.equipment.relic];
    return ids.map((id) => id ? this.reg.items.get(id) : undefined).filter((item): item is ItemDef => item !== undefined);
  }

  private showObservatory(): void {
    if (!this.observatorySession) {
      this.leaveCoop();
      this.pendingRunSetup = null;
      this.sim = null;
      this.recorder = null;
      this.replayPlayer = null;
      const character = characterDef(this.save.selectedCharacter).id;
      this.observatorySession = new ObservatorySession(this.save.pets.owned, character, this.save.house.cosmetics, this.save.pets.assignments, this.save.house.placements);
      this.input.setPlayerCount(1);
      this.view.enterObservatory(this.observatorySession, character, this.currentEquipment());
      this.audio.setMusic('hub');
      this.touch?.setAbilityLabel(this.save.selectedCharacter);
      this.touch?.setHouseMode(true);
    }
    this.returnToObservatory = false;
    this.mount(observatoryHud(() => this.pauseObservatory(), () => this.showObservatoryBuilder()), 'observatory');
    this.updateObservatoryPrompt();
  }

  private resumeObservatory(): void {
    if (!this.observatorySession) return this.showObservatory();
    const character = characterDef(this.save.selectedCharacter).id;
    const petIds = this.save.pets.owned;
    const currentPets = this.observatorySession.pets.map((pet) => pet.id);
    const cosmetics = this.save.house.cosmetics;
    const assignments = this.save.pets.assignments;
    const sessionAssignments = this.observatorySession.pets.map((pet) => `${pet.id}:${pet.assignedStation ?? ''}`).join('|');
    const savedAssignments = petIds.map((id) => `${id}:${Object.entries(assignments).find(([, pet]) => pet === id)?.[0] ?? ''}`).join('|');
    if (this.observatorySession.character !== character || currentPets.join('|') !== petIds.join('|') || this.observatorySession.cosmetics.join('|') !== cosmetics.join('|') || sessionAssignments !== savedAssignments) {
      const { x, z } = this.observatorySession.player;
      this.observatorySession = new ObservatorySession(petIds, character, cosmetics, assignments, this.save.house.placements);
      this.observatorySession.setPlayerPosition(x, z);
      this.view.enterObservatory(this.observatorySession, character, this.currentEquipment());
      this.touch?.setAbilityLabel(character);
    }
    this.touch?.setBuildMode(false, character);
    this.mount(observatoryHud(() => this.pauseObservatory(), () => this.showObservatoryBuilder()), 'observatory');
    this.updateObservatoryPrompt();
  }

  private pauseObservatory(): void {
    if (!this.observatorySession || this.screen !== 'observatory') return;
    this.mount(observatoryPauseScreen({
      resume: () => this.resumeObservatory(),
      character: () => { this.returnToObservatory = true; this.showHub('characters'); },
      menu: () => this.showMenu(),
    }), 'observatoryPaused');
  }

  private leaveObservatory(): void {
    if (!this.observatorySession) return;
    this.view.leaveObservatory();
    this.observatorySession = null;
    this.returnToObservatory = false;
    this.touch?.setHouseMode(false);
  }

  private updateObservatoryPrompt(): void {
    const prompt = this.overlay?.querySelector<HTMLElement>('[data-observatory-prompt]');
    if (!prompt || !this.observatorySession) return;
    const preview = this.observatorySession.buildPreview;
    if (preview) {
      prompt.textContent = preview.valid ? 'E / X · Place module · Ability rotates 90°' : 'Invalid placement · move clear of stations, walls, furniture, and the player';
      prompt.classList.toggle('valid', preview.valid);
      prompt.classList.toggle('invalid', !preview.valid);
      return;
    }
    const station = this.observatorySession.nearStation();
    prompt.textContent = station ? `E / X · ${station.name} — ${station.description}` : 'Explore the house and yard · approach a station to interact';
  }

  private showObservatoryBuilder(): void {
    if (!this.observatorySession) return;
    this.observatorySession.cancelBuild();
    this.touch?.setBuildMode(false, this.save.selectedCharacter);
    this.mount(observatoryBuildScreen(this.save, {
      place: (moduleId) => this.startObservatoryPlacement(moduleId),
      buy: (moduleId) => {
        const module = HOUSE_MODULE_BY_ID.get(moduleId);
        if (!module || this.save.chronoshards < module.cost) return;
        this.save.chronoshards -= module.cost;
        this.save.house.modules[moduleId] = (this.save.house.modules[moduleId] ?? 0) + 1;
        this.persist();
        this.audio.play('ui');
        this.startObservatoryPlacement(moduleId);
      },
      move: (id) => {
        const placement = this.save.house.placements.find((entry) => entry.id === id);
        if (placement) this.startObservatoryPlacement(placement.moduleId, id);
      },
      remove: (id) => {
        if (!this.observatorySession?.removePlacement(id)) return;
        this.commitObservatoryLayout();
        this.showObservatoryBuilder();
      },
      back: () => this.resumeObservatory(),
    }), 'observatoryMenu');
  }

  private startObservatoryPlacement(moduleId: string, editingId: string | null = null): void {
    if (!this.observatorySession?.beginBuild(moduleId, editingId)) return;
    const module = HOUSE_MODULE_BY_ID.get(moduleId)!;
    this.touch?.setBuildMode(true, this.save.selectedCharacter);
    this.mount(observatoryPlacementHud(module.name, this.observatorySession.buildPreview?.valid ?? false, () => {
      this.observatorySession?.cancelBuild();
      this.showObservatoryBuilder();
    }), 'observatory');
    this.updateObservatoryPrompt();
  }

  private commitObservatoryLayout(): void {
    if (!this.observatorySession) return;
    this.save.house.placements = this.observatorySession.placements.map((placement) => ({ ...placement }));
    this.save.house.layoutRevision++;
    this.persist();
  }

  private interactObservatory(station: ObservatoryStation): void {
    switch (station.id) {
      case 'chronolab':
      case 'garden':
        this.showObservatoryUpgrade(station.id);
        break;
      case 'wardrobe':
        this.returnToObservatory = true;
        this.showHub('characters');
        break;
      case 'pets':
        this.returnToObservatory = true;
        this.showHub('pets');
        break;
      case 'timeline': {
        this.returnToObservatory = true;
        const setup = this.save.runSetup;
        this.showRunSetup({ startEra: setup.startEra, paradox: setup.paradox, players: setup.players, daily: false });
        break;
      }
      case 'telescope':
        this.hud.toast('The Chronoscope is ready for a future Observatory expansion.');
        break;
    }
  }

  private showObservatoryUpgrade(id: 'chronolab' | 'garden'): void {
    const upgrade = () => {
      const tier = this.save.house.rooms[id] ?? 0;
      const cost = (id === 'chronolab' ? 150 : 120) * (tier + 1);
      if (tier >= 3 || this.save.chronoshards < cost) return;
      this.save.chronoshards -= cost;
      this.save.house.rooms[id] = tier + 1;
      this.persist();
      this.audio.play('ui');
      this.showObservatoryUpgrade(id);
    };
    this.mount(observatoryUpgradeScreen(this.save, id, { upgrade, back: () => this.resumeObservatory() }), 'observatoryMenu');
  }

  private showRunSetup(options: RunSetupOptions): void {
    this.pendingRunSetup = options;
    this.audio.setMusic('hub');
    this.mount(runSetupScreen(this.save, this.reg, this.view.characters, options, {
      back: () => {
        if (this.returnToObservatory && this.observatorySession) {
          this.pendingRunSetup = null;
          this.returnToObservatory = false;
          this.resumeObservatory();
        } else this.showMenu();
      },
      confirm: () => this.startRun(options),
      changed: () => this.persist(),
      edit: (tab) => this.showHub(tab),
    }), 'runSetup');
  }

  /* ---------------------------------------------------------------- run */

  startRun(o: { startEra: number; paradox: number; players: number; daily: boolean }): void {
    this.leaveObservatory();
    this.pendingRunSetup = null;
    this.challengeId = null;
    this.isDaily = o.daily;
    this.dailyRunKey = o.daily ? dailyKey() : null;
    this.dailyPractice = !!this.dailyRunKey && this.save.daily[this.dailyRunKey] !== undefined;
    if (!o.daily) clearRun();
    const cfg = o.daily
      ? buildDailyRunConfig(this.reg, this.dailyRunKey!)
      : buildRunConfig(this.save, this.reg, { seed: (Math.random() * 0xffffffff) >>> 0, players: o.players, startEra: o.startEra, paradox: o.paradox });
    cfg.introSeconds = INTRO_SECONDS;
    this.sim = new Sim(this.reg, cfg);
    this.recorder = o.daily ? null : recorderFor(this.sim, GAME_VERSION);
    this.replayPlayer = null;
    this.runSaveTimer = 0;
    this.input.setPlayerCount(o.players);
    this.view.setPlayerCount(o.players, cfg.characters, cfg.equipment);
    this.touch?.setAbilityLabel(cfg.characters?.[0] ?? 'knight');
    this.view.setEra(this.sim.currentEra);
    this.view.instanced.setBoss(null);
    this.audio.setMusic('stage');
    this.view.damageNumbers?.clear();
    this.mount(null, 'run');
    this.hud.toast(o.daily ? `Daily ${this.dailyPractice ? 'practice' : 'attempt'} · ${this.sim.currentEra.name}` : `${this.sim.currentEra.name}`);
  }

  private startChallenge(id: string): void {
    const challenge = CHALLENGE_BY_ID.get(id);
    if (!challenge) return;
    clearRun();
    this.isDaily = false;
    this.dailyRunKey = null;
    this.dailyPractice = false;
    this.challengeId = id;
    const cfg = buildChallengeRunConfig(this.reg, challenge);
    cfg.introSeconds = INTRO_SECONDS;
    this.sim = new Sim(this.reg, cfg);
    this.recorder = recorderFor(this.sim, GAME_VERSION);
    this.replayPlayer = null;
    this.runSaveTimer = 0;
    this.input.setPlayerCount(1);
    this.view.setPlayerCount(1, cfg.characters, cfg.equipment);
    this.touch?.setAbilityLabel(cfg.characters?.[0] ?? 'knight');
    this.view.setEra(this.sim.currentEra);
    this.view.instanced.setBoss(null);
    this.audio.setMusic('stage');
    this.view.damageNumbers?.clear();
    this.mount(null, 'run');
    this.hud.toast(`Challenge · ${challenge.name}`);
  }

  private resumeRun(): void {
    const saved = loadRun(GAME_VERSION);
    if (!saved) {
      this.hud.toast('That run can no longer be resumed.');
      this.showMenu();
      return;
    }
    try {
      const sim = new Sim(this.reg, restoreConfig(saved.config, this.reg));
      restore(sim, storedSnapshot(saved));
      this.sim = sim;
      this.isDaily = false;
      this.dailyRunKey = null;
      this.dailyPractice = false;
      this.challengeId = sim.config.challengeId ?? null;
      const replay = saved.replay as ReplayData | undefined;
      // A quota fallback may preserve the run without its earlier input log.
      // Do not create a misleading partial replay that starts at this snapshot.
      this.recorder = replay?.version === 1 && replay.gameVersion === GAME_VERSION ? ReplayRecorder.from(replay) : null;
      this.replayPlayer = null;
      this.runSaveTimer = 0;
      this.input.setPlayerCount(sim.config.playerCount);
      this.view.setPlayerCount(sim.config.playerCount, sim.config.characters, sim.config.equipment);
      this.touch?.setAbilityLabel(sim.config.characters?.[0] ?? 'knight');
      this.view.setEra(sim.currentEra);
      this.view.instanced.setBoss(sim.ctx.boss?.def ?? null);
      this.view.damageNumbers?.clear();
      this.audio.setMusic('stage');
      if (sim.run.phase === 'levelup') this.mount(this.levelUp(sim), 'levelup');
      else this.mount(null, 'run'); // a restored clear phase resumes with its portals live
      this.hud.toast(`Run restored · ${sim.currentEra.name}`);
    } catch (error) {
      console.warn('run restore failed; discarding', error);
      clearRun();
      this.showMenu();
      this.hud.toast('The saved run was incompatible and was discarded.');
    }
  }

  private watchReplay(): void {
    const replay = loadReplay(GAME_VERSION);
    if (!replay) {
      this.hud.toast('No compatible replay is available.');
      return;
    }
    this.watchReplayData(replay, 'last run');
  }

  private watchReplayData(replay: ReplayData, label: string): void {
    if (replay.gameVersion !== GAME_VERSION) {
      this.hud.toast('That replay was recorded with an incompatible version.');
      return;
    }
    try {
      const sim = new Sim(this.reg, restoreConfig(replay.config, this.reg));
      for (const entry of replay.commands) sim.queueCommand(entry.command, entry.tick);
      this.sim = sim;
      this.isDaily = false;
      this.recorder = null;
      this.replayPlayer = new ReplayPlayer(replay);
      this.view.setPlayerCount(sim.config.playerCount, sim.config.characters, sim.config.equipment);
      this.view.setEra(sim.currentEra);
      this.view.instanced.setBoss(null);
      this.view.damageNumbers?.clear();
      this.audio.setMusic('stage');
      this.mount(h('div', { class: 'attract' }, h('div', { class: 'attract-badge' }, 'REPLAY'), exitHint('exit')), 'replay');
      this.hud.toast(`Watching ${label}`);
    } catch (error) {
      console.warn('replay failed', error);
      this.showMenu();
      this.hud.toast('That replay could not be played.');
    }
  }

  private saveActiveRun(): void {
    const sim = this.sim;
    if (!sim || this.isDaily || this.onlineRun || this.screen === 'attract' || this.screen === 'replay' || sim.run.phase === 'dead' || sim.run.phase === 'victory') return;
    // A long analog-input replay can outgrow a browser's localStorage quota.
    // Never let the optional replay prevent the recoverable run snapshot.
    if (!saveRun(sim, GAME_VERSION, this.recorder?.data())) {
      this.recorder = null;
      saveRun(sim, GAME_VERSION);
    }
  }

  private applyRunCommand(command: SimCommand): boolean {
    const sim = this.sim;
    if (!sim) return false;
    const tick = sim.world.tick;
    if (!sim.applyCommand(command)) return false;
    this.recorder?.command(tick, command);
    this.saveActiveRun();
    return true;
  }

  private tickReplay(dt: number): void {
    const sim = this.sim;
    const frames = this.replayPlayer?.next();
    if (!sim || !frames) {
      this.showMenu();
      return;
    }
    sim.tick(dt, frames);
    this.view.handleEvents(sim.events.drain(), sim);
    this.view.setEra(sim.currentEra);
    if (sim.run.phase === 'dead' || sim.run.phase === 'victory') {
      const expected = this.replayPlayer?.data.finalHash;
      const verified = expected === undefined || expected === sim.hashState();
      this.hud.toast(verified ? (sim.run.phase === 'victory' ? 'Replay verified · victory' : 'Replay verified') : 'Replay desynced · result rejected');
      this.showMenu();
    }
  }

  private tick(dt: number): void {
    if (this.screen === 'menu') {
      this.idleSec += dt;
      if (this.idleSec >= ATTRACT_IDLE_SEC && !this.debug.visible) this.startAttract();
      return;
    }
    if (this.observatorySession) {
      if (this.screen === 'observatory') {
        this.input.sample(this.observatoryInput);
        screenToWorldMovement(this.observatoryInput[0], this.view.renderer.camYaw);
        if (this.observatorySession.buildPreview) {
          const placed = this.observatorySession.tickBuild(dt, this.observatoryInput[0]);
          if (placed) {
            this.commitObservatoryLayout();
            this.audio.play('ui');
            this.showObservatoryBuilder();
          }
          this.updateObservatoryPrompt();
          return;
        }
        const station = this.observatorySession.tick(dt, this.observatoryInput[0]);
        if (this.observatorySession.player.abilityPulse) this.view.playObservatoryAbility(this.observatorySession);
        this.updateObservatoryPrompt();
        if (station) this.interactObservatory(station);
      }
      return;
    }
    if (this.screen === 'attract') {
      this.tickAttract(dt);
      return;
    }
    if (this.screen === 'replay') {
      this.tickReplay(dt);
      return;
    }
    if (this.onlineRun) {
      if (this.screen !== 'results') this.tickOnline(dt);
      else {
        // Keep the lockstep flowing behind the results screen: a teammate a few
        // frames behind still needs our bundles (host) and hash checks, and the
        // run can only end identically everywhere if nobody stops early.
        this.input.sample(this.coopInput);
        this.coop!.step(this.coopInput[0]);
        this.sim?.events.drain();
      }
      return;
    }
    const sim = this.sim;
    if (!sim || this.screen !== 'run') return;
    this.input.sample(sim.inputs);
    for (const frame of sim.inputs) screenToWorldMovement(frame, this.view.renderer.camYaw);
    this.recorder?.frame(sim.inputs);
    const serial = sim.run.advanceSerial;
    sim.tick(dt);
    this.view.handleEvents(sim.events.drain(), sim);
    // Walking into a portal advances inside the sim; present it here.
    if (sim.run.advanceSerial !== serial) {
      this.onAdvanced(sim);
      return;
    }
    switch (sim.run.phase) {
      case 'levelup':
        this.mount(this.levelUp(sim), 'levelup');
        break;
      case 'dead':
      case 'victory':
        this.finishRun();
        break;
    }
    if ((this.saveTimer += dt) > 30) {
      this.saveTimer = 0;
      this.save.stats.playtimeSec += 30;
    }
    if (!this.isDaily && (this.runSaveTimer += dt) >= 10) {
      this.runSaveTimer = 0;
      this.saveActiveRun();
    }
    if (this.zoomPersistTimer >= 0 && (this.zoomPersistTimer -= dt) <= 0) {
      this.zoomPersistTimer = -1;
      this.persist();
    }
  }

  /** Level-up screen bound to whichever player owes a pick (co-op: one at a time). */
  private levelUp(sim: Sim): HTMLElement {
    const p = Math.max(0, sim.pickingPlayer());
    return levelUpScreen(
      sim,
      (id) => this.pick(id),
      () => this.applyRunCommand({ type: 'reroll', player: p }),
      (id) => this.applyRunCommand({ type: 'banish', player: p, id }),
    );
  }

  private pick(id: string): void {
    const sim = this.sim!;
    // every phase-changing action goes through applyCommand — the same path a
    // host will replay a guest's commands through (docs/MULTIPLAYER.md)
    if (!this.applyRunCommand({ type: 'chooseUpgrade', player: Math.max(0, sim.pickingPlayer()), id })) return;
    this.audio.play('ui');
    if (sim.run.phase === 'levelup') this.mount(this.levelUp(sim), 'levelup');
    else this.mount(null, 'run');
  }

  /** The party just travelled through a portal (sim-driven): toast, or the 3D time jump between eras. */
  private onAdvanced(sim: Sim): void {
    const wasEra = sim.run.lastAdvanceEra;
    this.view.instanced.setBoss(null);
    this.view.setWarp(0);
    const resume = () => {
      this.hud.toast(`${sim.currentEra.name} · ${sim.currentStage.name}`);
      if (sim.run.phase === 'levelup') this.mount(this.levelUp(sim), 'levelup');
      else this.mount(null, 'run');
    };
    if (wasEra) {
      // 3D time jump (render/view.ts): the sim is held on the 'timeJump' screen until the view reports done.
      this.audio.setMusic('none');
      this.mount(h('div', { class: 'jump-overlay' }, h('div', { class: 'jump-title' }, sim.currentEra.name), h('div', { class: 'sub' }, sim.currentEra.subtitle), h('button', { class: 'small jump-skip', onClick: () => this.view.skipTimeJump() }, 'Skip transition')), 'timeJump');
      this.view.timeJump(sim.currentEra, () => {
        this.audio.setMusic('stage');
        resume();
      });
    } else resume();
  }

  /* ---------------------------------------------------------------- attract mode */

  private onAnyInput(kind: string): void {
    this.idleSec = 0;
    if (this.screen === 'attract') this.showMenu();
    else if (this.screen === 'replay' && kind !== 'pointermove') this.showMenu();
    // Online, the time jump is a deterministic hold on every peer, so it can't be skipped.
    else if (this.screen === 'timeJump' && !this.onlineRun && (kind === 'keydown' || kind === 'pointerdown' || kind === 'gamepad')) this.view.skipTimeJump();
  }

  /** Bot-driven demo run behind a "DEMO" banner; any input returns to the menu. */
  private startAttract(): void {
    const eras = this.reg.eras.filter((e) => e.implemented);
    const maxAnchor = Math.max(0, ...this.save.anchors.map((x) => x + 1));
    const startEra = Math.min(eras.length - 1, Math.floor(Math.random() * (maxAnchor + 1)));
    const seed = (Math.random() * 0xffffffff) >>> 0;
    const cfg = buildRunConfig(this.save, this.reg, { seed, players: 1, startEra, paradox: 0 });
    cfg.introSeconds = INTRO_SECONDS;
    this.sim = new Sim(this.reg, cfg);
    this.view.setPlayerCount(1, cfg.characters, cfg.equipment);
    this.view.setEra(this.sim.currentEra);
    this.view.instanced.setBoss(null);
    this.view.damageNumbers?.clear();
    this.audio.setMusic('stage');
    this.mount(h('div', { class: 'attract' }, h('div', { class: 'attract-badge' }, 'DEMO'), exitHint('play')), 'attract');
    this.hud.setVisible(true);
    this.hud.toast(`${this.sim.currentEra.name}`);
  }

  private tickAttract(dt: number): void {
    const sim = this.sim;
    if (!sim) return;
    if (this.view.jumping) return;
    botInput(sim, 0, sim.inputs[0]);
    const serial = sim.run.advanceSerial;
    sim.tick(dt);
    this.view.handleEvents(sim.events.drain(), sim);
    if (sim.run.advanceSerial !== serial) {
      this.view.instanced.setBoss(null);
      if (sim.run.lastAdvanceEra) this.view.timeJump(sim.currentEra, () => this.hud.toast(sim.currentEra.name));
      return;
    }
    switch (sim.run.phase) {
      case 'levelup':
        sim.applyCommand({ type: 'chooseUpgrade', player: Math.max(0, sim.pickingPlayer()), id: botPickUpgrade(sim) });
        break;
      case 'stageClear':
      case 'eraClear':
        // The bot walks into the stable portal (core/bot.ts); a safety net if it somehow can't.
        if (sim.run.portals.length > 0 && sim.run.portalIdle[0] > 12) sim.applyCommand({ type: 'advanceStage' });
        break;
      case 'dead':
      case 'victory':
        this.startAttract(); // loop another demo
        break;
    }
  }

  private finishRun(): void {
    const sim = this.sim!;
    const runWasDaily = this.isDaily;
    const runWasPractice = this.dailyPractice;
    const runDailyKey = this.dailyRunKey;
    const completedReplay = !runWasDaily && this.recorder ? this.recorder.data(sim.hashState()) : undefined;
    const result = applyRunResult(this.save, sim);
    result.newAchievements = updateAchievements(this.save, result);
    if (!runWasDaily) clearRun();
    if (completedReplay) saveReplay(completedReplay);
    if (runWasDaily && !runWasPractice && runDailyKey) this.save.daily[runDailyKey] = result.score;
    this.runResult = result;
    this.persist();
    this.view.setWarp(0);
    const dailyMessage = runWasDaily
      ? runWasPractice
        ? 'Daily practice · score is not submitted'
        : 'Daily scored attempt · submitting…'
      : undefined;
    const results = resultsScreen(
      result,
      sim,
      () => this.showHub(),
      () => sim.config.challengeId ? this.startChallenge(sim.config.challengeId) : this.startRun({ startEra: sim.run.startEra, paradox: sim.run.paradox, players: sim.config.playerCount, daily: runWasDaily }),
      dailyMessage,
    );
    this.mount(
      results,
      'results',
    );
    const submissionStatus = results.querySelector<HTMLElement>('.run-submit-status');
    if (!runWasPractice && !sim.config.challengeId) {
      void this.backend
        .submitRun({
          seed: sim.run.seed,
          dailyKey: runWasDaily ? runDailyKey : null,
          score: result.score,
          erasCleared: result.erasCleared,
          bossesKilled: result.bossesKilled,
          highestEra: result.highestEra,
          kills: result.kills,
          level: result.level,
          timeSurvivedSec: Math.round(result.timeSurvivedSec),
          victory: result.victory,
          paradox: sim.run.paradox,
          playerCount: sim.config.playerCount,
          startEra: sim.run.startEra,
          upgrades: sim.run.upgrades.flatMap((m) => [...m.entries()].flatMap(([id, n]) => Array(n).fill(id) as string[])),
          character: sim.config.characters?.[0] ?? 'knight',
          equipment: (sim.config.equipment[0] ?? []).map((item) => item.id),
          version: GAME_VERSION,
          replay: completedReplay,
        })
        .then((response) => {
          if (!runWasDaily || !runDailyKey) return;
          if (response.score !== undefined) this.save.daily[runDailyKey] = response.score;
          this.persist();
          if (submissionStatus) submissionStatus.textContent = response.accepted ? 'Daily score submitted' : response.reason === 'daily attempt already used' ? 'Daily practice · today’s scored attempt was already used' : `Daily score not submitted · ${response.reason ?? 'unknown error'}`;
        })
        .catch((error: unknown) => {
          console.warn('submit failed', error);
          if (submissionStatus && runWasDaily) submissionStatus.textContent = 'Daily score not submitted · connection failed';
        });
    }
    if (result.newAchievements.length) {
      const names = result.newAchievements.map((id) => ACHIEVEMENTS.find((a) => a.id === id)?.name ?? id);
      this.hud.toast(`Achievement unlocked: ${names.join(', ')}`);
    }
  }

  /* ---------------------------------------------------------------- online co-op */

  /** True while the active sim belongs to an online co-op session. */
  private get onlineRun(): boolean {
    return !!this.coop?.sim && this.sim === this.coop.sim;
  }

  private coopName(): string {
    try {
      const stored = localStorage.getItem(COOP_NAME_KEY);
      if (stored) return stored;
    } catch {
      /* storage disabled */
    }
    const user = this.backend?.currentUser();
    return user && !user.anonymous ? user.name : 'Traveler';
  }

  private saveCoopName(name: string): void {
    try {
      localStorage.setItem(COOP_NAME_KEY, name);
    } catch {
      /* storage disabled */
    }
  }

  private makeTransport(): LobbyTransport {
    return LOCAL_COOP ? new LocalLobbyTransport() : new CloudLobbyTransport(lobbyApi(() => this.backend.idToken(), (import.meta as { env?: ImportMetaEnv }).env?.VITE_API_BASE_URL ?? ''), GAME_VERSION);
  }

  private coopCallbacks(): CoopCallbacks {
    return {
      onLobby: () => this.onCoopLobby(),
      onStart: (sim, index, names) => this.startOnlineRun(sim, index, names),
      onPlayerLeft: (_index, name) => this.hud.toast(`${name} left`),
      onPlayerRejoined: (_index, name) => this.hud.toast(`${name} rejoined`),
      onEnded: (reason) => this.onCoopEnded(reason),
    };
  }

  showOnline(message?: string): void {
    this.leaveCoop();
    this.sim = null;
    this.view.setPlayerCount(0);
    this.audio.setMusic('hub');
    const availability = LOCAL_COOP ? 'ready' : this.backend.kind !== 'firebase' ? 'offline' : this.backend.currentUser() ? 'ready' : 'signin';
    this.mount(onlineEntryScreen({
      availability,
      local: LOCAL_COOP,
      name: this.coopName(),
      code: this.lastCoopCode(),
      message,
      host: (name) => void this.hostOnline(name),
      join: (code, name) => void this.joinOnline(code, name),
      signIn: (kind) => void (kind === 'google' ? this.backend.signInWithGoogle() : this.backend.signInAnonymously())
        .then(() => this.syncFromCloud())
        .then(() => this.showOnline())
        .catch((e: unknown) => this.showOnline((e as Error).message)),
      back: () => this.showMenu(),
    }), 'online');
  }

  private async hostOnline(name: string): Promise<void> {
    this.saveCoopName(name);
    const settings = clampCoopSettings(this.save, this.reg, { startEra: this.save.runSetup.startEra, paradox: this.save.runSetup.paradox });
    const session = new CoopHostSession(this.reg, GAME_VERSION, this.coopCallbacks(), this.makeTransport(), loadoutFromSave(this.save, this.reg, name), settings);
    this.coop = session;
    try {
      await session.open();
      if (this.coop === session) this.showLobby();
    } catch (e) {
      if (this.coop === session) this.showOnline((e as Error).message);
    }
  }

  /** The code last joined in this tab, so a player who crashed or reloaded can rejoin in one click. */
  private lastCoopCode(): string {
    try {
      return sessionStorage.getItem('chronofall.lastCoopCode') ?? '';
    } catch {
      return '';
    }
  }

  private async joinOnline(code: string, name: string): Promise<void> {
    this.saveCoopName(name);
    try {
      sessionStorage.setItem('chronofall.lastCoopCode', code);
    } catch {
      /* storage disabled */
    }
    const session = new CoopGuestSession(this.reg, GAME_VERSION, this.coopCallbacks(), this.makeTransport(), loadoutFromSave(this.save, this.reg, name));
    this.coop = session;
    const status = (text: string) => {
      const el = this.overlay?.querySelector('.coop-status');
      if (el) el.textContent = text;
    };
    try {
      await session.join(code, status);
      if (this.coop === session && !session.sim) this.showLobby();
    } catch (e) {
      if (this.coop === session) {
        this.coop = null;
        session.leave();
        this.showOnline((e as Error).message);
      }
    }
  }

  private showLobby(): void {
    const coop = this.coop;
    if (!coop) return this.showOnline();
    this.pendingLobby = false;
    this.audio.setMusic('hub');
    const lobby = lobbyScreen(coop, this.save, this.reg, {
      start: () => {
        if (coop.role === 'host') coop.start();
      },
      setReady: (ready) => {
        if (coop.role === 'guest') coop.setReady(ready);
        this.coopLobby?.refresh();
      },
      setSettings: (startEra, paradox) => {
        if (coop.role === 'host') coop.setSettings(clampCoopSettings(this.save, this.reg, { startEra, paradox }));
      },
      editLoadout: () => {
        this.pendingLobby = true;
        this.showHub('characters');
      },
      leave: () => this.showOnline(),
    });
    this.coopLobby = lobby;
    this.mount(lobby.root, 'lobby');
    window.clearInterval(this.coopLobbyTimer);
    this.coopLobbyTimer = window.setInterval(() => {
      if (this.coop !== coop) return window.clearInterval(this.coopLobbyTimer);
      if (!coop.sim) coop.tickLobby();
    }, 1000);
  }

  private onCoopLobby(): void {
    const coop = this.coop;
    if (!coop) return;
    // A guest whose host went back to the lobby mid-results (or mid-run) wraps up locally first.
    if (coop.role === 'guest' && this.onlineRun && this.screen !== 'results') {
      this.finishOnlineRun('The host returned to the lobby.');
      return;
    }
    if (this.screen === 'lobby') this.coopLobby?.refresh();
  }

  private onCoopEnded(reason: string): void {
    if (this.onlineRun && this.screen !== 'results') this.finishOnlineRun(reason);
    else if (this.screen === 'lobby' || this.screen === 'online' || (this.screen === 'hub' && this.pendingLobby)) this.showOnline(reason);
  }

  /** Tear down any co-op session and the presentation state it changed. */
  private leaveCoop(): void {
    window.clearInterval(this.coopLobbyTimer);
    if (this.coop) {
      this.coop.leave();
      this.coop = null;
    }
    this.coopLobby = null;
    this.pendingLobby = false;
    this.coopScreenKey = '';
    this.coopPickPending = false;
    this.loop.keepAliveInBackground = false;
    this.view.followPlayer = null;
    this.hud.localPlayer = 0;
    this.hud.setParty(null);
    this.hud.setNetStatus(null);
    this.debug.netInfo = null;
    this.debug.commandsEnabled = true;
  }

  private startOnlineRun(sim: Sim, index: number, names: string[]): void {
    const coop = this.coop;
    const lockstep = coop?.lockstep;
    if (!coop || !lockstep) return;
    window.clearInterval(this.coopLobbyTimer);
    this.coopLobby = null;
    this.pendingRunSetup = null;
    this.pendingLobby = false;
    this.isDaily = false;
    this.dailyRunKey = null;
    this.dailyPractice = false;
    this.challengeId = null;
    this.sim = sim;
    this.recorder = null;
    this.replayPlayer = null;
    lockstep.recorder = recorderFor(sim, GAME_VERSION);
    lockstep.onCommand = (player, cmd, ok) => this.onCoopCommand(player, cmd, ok);
    lockstep.onEraJump = () => this.startCoopJump();
    if (coop.role === 'guest' && coop.lockstep) {
      coop.lockstep.onResync = () => {
        this.view.setEra(sim.currentEra);
        this.view.instanced.setBoss(sim.ctx.boss?.def ?? null);
        this.coopScreenKey = '';
      };
    }
    this.coopPickPending = false;
    this.coopScreenKey = '';
    this.input.setPlayerCount(1);
    this.view.setPlayerCount(sim.config.playerCount, sim.config.characters, sim.config.equipment);
    this.view.followPlayer = index;
    this.hud.localPlayer = index;
    this.hud.setParty(names);
    this.touch?.setAbilityLabel(sim.config.characters?.[index] ?? 'knight');
    this.view.setEra(sim.currentEra);
    this.view.instanced.setBoss(null);
    this.view.damageNumbers?.clear();
    this.audio.setMusic('stage');
    this.loop.keepAliveInBackground = true;
    this.debug.commandsEnabled = coop.role === 'host';
    this.debug.netInfo = () => [...coop.netDebug(), `  step ${this.coopStepMs.toFixed(2)} ms · fps ${this.loop.fps.toFixed(0)} · tps ${this.loop.tps.toFixed(0)}`];
    this.mount(null, 'run');
    this.hud.toast(sim.currentEra.name);
  }

  private tickOnline(dt: number): void {
    const coop = this.coop!;
    const sim = this.sim!;
    // Always sample so edges don't linger; InputManager zeroes input while a menu is open.
    this.input.sample(this.coopInput);
    screenToWorldMovement(this.coopInput[0], this.view.renderer.camYaw);
    const t0 = performance.now();
    const executed = coop.step(this.coopInput[0]);
    this.coopStepMs += (performance.now() - t0 - this.coopStepMs) * 0.05;
    if (this.coop !== coop || this.sim !== sim) return; // the session ended inside step()
    if (executed > 0) {
      const events = sim.events.drain();
      this.view.handleEvents(events, sim);
      // Mid-era portal travel just toasts; era travel runs the held time jump (lockstep onEraJump).
      if (events.some((e) => e.type === 'stageAdvanced' && !e.era)) {
        this.view.instanced.setBoss(null);
        this.hud.toast(`${sim.currentEra.name} · ${sim.currentStage.name}`);
      }
    }
    const net = coop.netStatus();
    this.hud.setNetStatus(net.text, net.warn);
    this.syncOnlineScreen();
    if ((this.saveTimer += dt) > 30) {
      this.saveTimer = 0;
      this.save.stats.playtimeSec += 30;
    }
  }

  /** Mount whatever overlay the shared sim's phase calls for, from this player's point of view. */
  private syncOnlineScreen(): void {
    const coop = this.coop!;
    const sim = this.sim!;
    const r = sim.run;
    const me = coop.localIndex;
    if (r.phase === 'dead' || r.phase === 'victory') return this.finishOnlineRun();
    if (this.screen === 'timeJump' || this.screen === 'paused' || this.view.jumping) return;
    let key: string;
    let build: () => [HTMLElement | null, Screen];
    if (r.phase === 'levelup') {
      // Picks are simultaneous: show my cards while I owe one, otherwise who we're waiting on.
      const picking = sim.pickingPlayers();
      if (r.pendingPicks[me] > 0) {
        key = `lv:${r.level}:${r.pendingPicks[me]}:${sim.offersFor(me).map((o) => o.id).join(',')}:${r.rerollsLeft[me]}:${r.banishesLeft[me]}:${this.coopPickPending}`;
        build = () => {
          const el = levelUpScreen(
            sim,
            (id) => this.coopIssue({ type: 'chooseUpgrade', player: me, id }),
            () => this.coopIssue({ type: 'reroll', player: me }),
            (id) => this.coopIssue({ type: 'banish', player: me, id }),
            me,
            coop.names[me],
          );
          if (this.coopPickPending) el.classList.add('coop-pending');
          return [el, 'levelup'];
        };
      } else {
        key = `lvwait:${picking.join(',')}`;
        const who = picking.map((p) => coop.names[p] ?? `Player ${p + 1}`);
        build = () => [waitingBanner(`Waiting for ${who.length > 1 ? `${who.slice(0, -1).join(', ')} and ${who[who.length - 1]}` : who[0]} to choose ${who.length > 1 ? 'upgrades' : 'an upgrade'}…`), 'coopWait'];
      }
    } else {
      key = 'run';
      build = () => [null, 'run'];
    }
    if (key === this.coopScreenKey) return;
    this.coopScreenKey = key;
    const [el, screen] = build();
    this.mount(el, screen);
  }

  private coopIssue(cmd: SimCommand): void {
    if (this.coopPickPending || !this.coop) return;
    this.coopPickPending = true;
    this.coop.issue(cmd);
    this.audio.play('ui');
    this.coopScreenKey = '';
  }

  private onCoopCommand(player: number, cmd: SimCommand, ok: boolean): void {
    const coop = this.coop;
    const sim = this.sim;
    if (!coop || !sim) return;
    if (player === coop.localIndex) {
      this.coopPickPending = false;
      this.coopScreenKey = '';
    }
    if (cmd.type === 'advanceStage' && ok) {
      this.view.instanced.setBoss(null);
      this.view.setWarp(0);
      if (sim.run.stageIndex !== 0) this.hud.toast(`${sim.currentEra.name} · ${sim.currentStage.name}`);
    }
  }

  /** Era advance: the lockstep holds every peer for the same number of frames while this plays. */
  private startCoopJump(): void {
    const sim = this.sim!;
    this.audio.setMusic('none');
    this.coopScreenKey = 'jump';
    this.mount(h('div', { class: 'jump-overlay' }, h('div', { class: 'jump-title' }, sim.currentEra.name), h('div', { class: 'sub' }, sim.currentEra.subtitle)), 'timeJump');
    this.view.timeJump(sim.currentEra, () => {
      this.audio.setMusic('stage');
      if (!this.onlineRun) return;
      this.coopScreenKey = '';
      this.mount(null, 'run');
      this.hud.toast(`${sim.currentEra.name} · ${sim.currentStage.name}`);
    });
  }

  /** Credit this player's profile from the shared run and show results (no leaderboard submission). */
  private finishOnlineRun(reason?: string): void {
    const coop = this.coop;
    const sim = this.sim;
    if (!coop || !sim) return;
    this.loop.keepAliveInBackground = false;
    this.view.skipTimeJump();
    const replay = coop.lockstep?.recorder?.data(sim.hashState());
    const result = applyRunResult(this.save, sim, coop.localIndex);
    result.newAchievements = updateAchievements(this.save, result);
    if (replay) saveReplay(replay);
    this.runResult = result;
    this.persist();
    this.view.setWarp(0);
    this.hud.setNetStatus(null);
    const status = reason ?? (sim.run.phase === 'victory' ? 'Your party restored the timeline.' : sim.run.phase === 'dead' ? 'The whole party fell.' : 'The online run ended.');
    const canRegroup = !coop.ended;
    this.mount(
      resultsScreen(result, sim, () => {
        this.leaveCoop();
        this.showHub();
      }, () => this.afterOnlineResults(), `${status} Online runs don’t post to the leaderboards.`, canRegroup ? 'Back to lobby' : 'Online menu'),
      'results',
    );
    if (result.newAchievements.length) {
      const names = result.newAchievements.map((id) => ACHIEVEMENTS.find((a) => a.id === id)?.name ?? id);
      this.hud.toast(`Achievement unlocked: ${names.join(', ')}`);
    }
  }

  private afterOnlineResults(): void {
    const coop = this.coop;
    if (!coop || coop.ended) return this.showOnline();
    this.sim = null;
    this.view.setPlayerCount(0);
    this.view.followPlayer = null;
    this.hud.setParty(null);
    this.hud.localPlayer = 0;
    this.coopScreenKey = '';
    if (coop.role === 'host') void coop.resetForLobby().then(() => this.coopLobby?.refresh());
    else coop.resetForLobby();
    this.showLobby();
  }

  private pause(): void {
    if (this.screen !== 'run') return;
    this.mount(
      pauseScreen(this.sim!, this.save.settings, {
        resume: () => {
          this.coopScreenKey = '';
          this.mount(null, 'run');
        },
        quit: () => {
          if (this.onlineRun) {
            this.coop?.leave();
            this.finishOnlineRun('You left the run.');
          } else this.finishRun();
        },
        setQuality: (q) => {
          this.save.settings.quality = q;
          this.view.renderer.setQuality(q);
          this.persist();
          this.pause();
        },
        setMusicVolume: (v) => {
          this.save.settings.musicVolume = v;
          this.audio.setMusicVolume(v);
          this.persist();
        },
        setSfxVolume: (v) => {
          this.save.settings.sfxVolume = v;
          this.audio.setSfxVolume(v);
          this.persist();
        },
        setCameraDistance: (v) => this.setCameraDistance(v, true),
        setColorblindMode: (mode) => this.setColorblindMode(mode),
        setControlsHud: (mode) => this.setControlsHud(mode),
        toggle: (key, on) => this.setToggle(key, on),
        controls: this.controlsActions(),
      }, this.onlineRun && this.coop ? { player: this.coop.localIndex, names: this.coop.names, online: true } : {}),
      'paused',
    );
  }

  private setToggle(key: 'screenShake' | 'showDamageNumbers' | 'highContrastProjectiles' | 'reducedFlash', on: boolean): void {
    this.save.settings[key] = on;
    this.view.shakeEnabled = this.save.settings.screenShake;
    this.applyPresentationSettings();
    this.persist();
  }

  private applyPresentationSettings(): void {
    this.view.shakeEnabled = this.save.settings.screenShake;
    if (this.view.damageNumbers) this.view.damageNumbers.enabled = this.save.settings.showDamageNumbers;
    this.view.renderer.setCameraDistance(this.save.settings.cameraDistance);
    this.view.renderer.reducedFlash = this.save.settings.reducedFlash;
    this.hud.setControlsMode(this.save.settings.controlsHud, this.save.stats.runs);
    if (this.view.instanced) {
      this.view.instanced.highContrastProjectiles = this.save.settings.highContrastProjectiles;
      this.view.instanced.reducedFlash = this.save.settings.reducedFlash;
      this.view.instanced.colorblindMode = this.save.settings.colorblindMode;
    }
  }

  private setCameraDistance(value: number, persistNow = false): void {
    const distance = clampCameraDistance(value);
    this.save.settings.cameraDistance = distance;
    this.view.renderer.setCameraDistance(distance);
    if (persistNow) this.persist();
    else this.zoomPersistTimer = 0.45;
  }

  private setColorblindMode(mode: ColorblindMode): void {
    this.save.settings.colorblindMode = mode;
    this.applyPresentationSettings();
    this.persist();
  }

  private setControlsHud(mode: ControlsHudMode): void {
    this.save.settings.controlsHud = mode;
    this.hud.setControlsMode(mode, this.save.stats.runs);
    this.persist();
  }

  private adjustCameraDistance(delta: number): void {
    this.setCameraDistance(this.save.settings.cameraDistance + delta);
  }

  private hotkey(code: string): void {
    if (code === 'F3' || code === 'Backquote') this.debug.toggle();
    else if (code !== 'Escape' && actionsForKey(this.save.settings.bindings, code).includes('pause')) {
      // A bound pause key only toggles pause; Escape keeps doing that plus every "back".
      if (this.screen === 'run') this.pause();
      else if (this.screen === 'observatory') this.observatorySession?.buildPreview ? (this.observatorySession.cancelBuild(), this.showObservatoryBuilder()) : this.pauseObservatory();
      else if (this.screen === 'paused') {
        this.coopScreenKey = '';
        this.mount(null, 'run');
      } else if (this.screen === 'observatoryPaused') this.resumeObservatory();
    } else if (code === 'Escape') {
      if (this.debug.visible) this.debug.setVisible(false);
      else if (this.screen === 'run') this.pause();
      else if (this.screen === 'observatory') this.observatorySession?.buildPreview ? (this.observatorySession.cancelBuild(), this.showObservatoryBuilder()) : this.pauseObservatory();
      else if (this.screen === 'paused') {
        this.coopScreenKey = '';
        this.mount(null, 'run');
      } else if (this.screen === 'observatoryPaused' || this.screen === 'observatoryMenu') this.resumeObservatory();
      else if (this.screen === 'runSetup') {
        if (this.returnToObservatory && this.observatorySession) {
          this.pendingRunSetup = null;
          this.returnToObservatory = false;
          this.resumeObservatory();
        } else this.showMenu();
      } else if (this.screen === 'online') this.showMenu();
      else if (this.screen === 'hub') this.hubBack();
    }
  }

  private render(alpha: number, frameDt: number): void {
    if ((this.screen === 'run' || this.screen === 'observatory') && !this.debug.visible) {
      const zoomAxis = this.input.zoomAxis();
      if (zoomAxis !== 0) this.adjustCameraDistance(zoomAxis * frameDt * 10);
    }
    if (this.observatorySession) this.view.frameObservatory(this.observatorySession, alpha, frameDt);
    else this.view.frame(this.sim, alpha, frameDt);
    if (this.sim && this.screen !== 'results') this.hud.update(this.sim);
    const info = this.view.renderer.renderer.info;
    this.debug.update(info.render.calls, info.render.triangles);
    this.gamepadNav.update();
    this.touch?.update();
  }
}
